import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'

import PropTypes from 'prop-types'
import lang from '@/lang'
import { useSelector, useDispatch } from 'react-redux'
import { AgGridReact } from 'ag-grid-react'
import { ClientSideRowModelModule, InfiniteRowModelModule } from 'ag-grid-community'
import { makeStyles } from '@mui/styles'
import icons from '@/modules/base/components/list/list-icons'
import BaseAmountCell from '@/modules/base/components/list/cell-renderer/base-amount-cell'
import CurrencyAmountCell from '@/modules/base/components/list/cell-renderer/currency-amount-cell'
import DateAmountCell from '@/modules/base/components/list/cell-renderer/date-amount-cell'
import DateTimeAmountCell from '@/modules/base/components/list/cell-renderer/date-time-amount-cell'
import DeliveryDateCell from '@/modules/base/components/list/cell-renderer/delivery-date-cell'
import DurationTimeCell from '@/modules/base/components/list/cell-renderer/duration-time-cell'
import CheckBoxCellRender from '@/modules/base/components/list/cell-renderer/checkbox-cell-renderer'
import HtmlCell from '@/modules/base/components/list/cell-renderer/html-cell'
import ContextMenuCell from '@/modules/base/components/context-menu/context-menu-cell/context-menu-cell'
import TagCell from '@/modules/base/components/list/cell-renderer/tag-cell'
import PercentageBarCell from '@/modules/base/components/list/cell-renderer/percentage-bar-cell'
import ListTagFilter from '@/modules/base/components/list/filter/list-tag-filter'
import { showContextMenu, hideContextMenu } from '@/redux/ducks/listContextMenu'
import { merge, isNumber, debounce } from 'lodash'
import ListColumnHeader from '@/modules/base/components/list/header/list-column-header'
import ListDateInput from '@/modules/base/components/list/inputs/list-date-input'
import PaginationV2 from '@/modules/base/components/list/pagination/pagination-v2'
import { ListDefaultExcelStyles, ListProps } from '@/modules/base/components/list/constants'
import shouldSkipRowClick from '@/modules/base/components/list/_helpers/should-skip-row-click'
import { getRowToRemember, shouldOpenByDefault } from '@/modules/base/components/list/_helpers/expanded-row-restore'
import TreeChildLoadingCell from '@/modules/base/components/list/cell-renderer/tree-child-loading-cell'
import Loader from '@/modules/base/components/loader/loader-inline'
import {
  ServerSideRowModelModule,
  SetFilterModule,
  ExcelExportModule,
  MenuModule,
  ClipboardModule,
  MultiFilterModule,
  RowGroupingModule,
} from 'ag-grid-enterprise'

import {
  updatePagination,
  updateFilterModel,
  updateSelectedNodes,
  clearList,
  setShallExport,
  setShallRefresh,
  setShallSoftRefresh,
  updateLastSelectedRowIndex,
  updateLastExpandedRowKey,
  saveColumnWidthInUserListSetting,
  LIST_TYPES,
} from '@/redux/ducks/lists'
import { DATE_FILTER_MODEL_KEY, itemMatchesDateFilters } from '@/modules/banking/_helpers/dateFilters'
import { useLocation } from 'react-router'
import clsx from 'clsx'
import StateCellRenderer from './cell-renderer/state-cell-renderer'

const ROW_SELECTION_SINGLE = 'single'
const ROW_SELECTION_MULTIPLE = 'multiple'

/** Names the renderers of the treeRows prop are registered under. */
const TREE_CHILD_CELL_RENDERER = 'treeChildCellRenderer'
const TREE_CHILD_LOADING_CELL_RENDERER = 'treeChildLoadingCellRenderer'

const TIMELINE_FILTER_LISTS = [
  LIST_TYPES.BANKING_LIST_TYPE,
  LIST_TYPES.BOOKING_LIST_TYPE,
]

/** Cell renderers that work on any grid, addressed by name from a column definition. */
const listCellRenderers = {
  amountCellRenderer: BaseAmountCell,
  checkBoxCellRender: CheckBoxCellRender,
  currencyAmountCellRenderer: CurrencyAmountCell,
  dateCellRenderer: DateAmountCell,
  dateTimeCellRenderer: DateTimeAmountCell,
  durationTimeCellRenderer: DurationTimeCell,
  tagCellRenderer: TagCell,
  percentageBarCellRenderer: PercentageBarCell,
  htmlCellRenderer: HtmlCell,
  genericStateCellRenderer: StateCellRenderer,
  deliveryDateCellRenderer: DeliveryDateCell,
}

const menuColumn = {
  headerName: '',
  field: 'menu',
  sortable: false,
  suppressResize: true,
  resizable: false,
  suppressMovable: true,
  filter: false,
  width: 85,
  minWidth: 85,
  maxWidth: 85,
  cellRenderer: 'contextMenuCellRenderer',
  suppressMenu: true,
  pinned: 'right',
}

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    height: '100%',
    color: theme.black1,
    display: 'flex',
    flexDirection: 'column',
  },
  /*
    Ties the child rows visually to the row they belong to: the expanded row and everything below it
    share one background. ag-grid maintains both classes itself, in the initial row classes and on
    every expand. Scoped to lists that actually use tree rows, because grouping rows carry
    ag-row-group-expanded as well and those grids must keep their look.
  */
  treeRows: {
    '& .ag-row-group-expanded, & .ag-row-level-1': {
      background: theme.primaryBrightBackground,
    },
  },
}))

const List = (props) => {

  /**
   * State to manage the grid API instance.
   * @type {[import('ag-grid-community').GridApi | null, React.Dispatch<React.SetStateAction<import('ag-grid-community').GridApi | null>>]}
   */
  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)
  const [gridInitialized, setGridInitialized] = useState(false)
  const location = useLocation()
  const classes = useStyles()
  const dispatch = useDispatch()

  const refContainer = useRef()
  const isApplyingGridFilterRef = useRef(false)
  const {
    contextMenu,
    contextMenuQuickActions = [],
    hasContextMenu = true,
    components,
    emptyComponent,
    emptyText = lang.list.noRowsToShow,
    loaderText = lang.list.loadingOoo,
    onListReady,
    showMenuContextMenu,
    rowSelection = ROW_SELECTION_MULTIPLE,
    suppressRowClickSelection = false,
    rowMultiSelectWithClick = true,
    disableRightClickContextMenu,
    customRightClickContextMenu,
    processCellForClipboard,
    isExternalFilterPresent,
    doesExternalFilterPass,
    hideMenuTabHeader = true,
    onSelectionChanged,
    disableCheckboxes = false,
    listSettingName,
    usePagination = true,
    enableSorting = true,
    rowModelType = ListProps.rowModelTypes.client,
    suppressServerSideInfiniteScroll = false,
    // Opt-in ag-grid option, default false. When false the key is omitted from the grid
    // options below, so every list that does NOT pass this prop keeps ag-grid's default
    // (async/animation-frame) rendering byte-identically. Only the useDocuAi expense list
    // opts in (see expense-list.tsx) — its heavier per-row review cells otherwise render a
    // frame behind ag-grid's animation-frame row insertion during fast scroll, briefly
    // showing cell-less "Ausgaben werden geladen" shells.
    suppressAnimationFrame = false,
    dataSource = null,
    toolPanels = ['filters'],
    showToolPanel = true,
    shouldClearOnUnmount = false,
    uniformRightClickAndKebabContextMenu = false,
    rowData: rowDataProp,
    onCellClicked,
    popupParent,
    treeRows,
  } = props
  const listState = useSelector(state => state.lists[listSettingName])
  const {
    searchTerm,
    quickFilter,
    dateFilters,
    filterModel,
    selectedNodes,
    setting: { autoSizeColumns = true, columns },
    pagination,
    shallExport,
    shallRefresh,
    shallSoftRefresh,
    exportItems,
    exportParams,
    isLoading,
    lastSelectedRowIndex,
    lastExpandedRowKey,
  } = listState

  const items = rowDataProp ?? listState.items ?? []
  const usesTimelineDateFilter = TIMELINE_FILTER_LISTS.includes(listSettingName)

  /*
    Held in a ref rather than read from the store on every render, because entering the list purges the
    grid and rebuilds every row node. Only that rebuild gives ag-grid the chance to ask
    isServerSideGroupOpenByDefault again, and it happens after onFirstDataRendered has already run — so
    the key has to outlive that event instead of being consumed by it.
  */
  const pendingExpandedKeyRef = useRef(lastExpandedRowKey)

  const isGridValid = () => {
    return gridApi?.destroyCalled === false
  }

  const getColumnFilterModel = useCallback(() => {
    const columnFilters = { ...(filterModel || {}), ...(quickFilter || {}) }

    if (usesTimelineDateFilter) {
      delete columnFilters[DATE_FILTER_MODEL_KEY]

      if (!quickFilter) {
        delete columnFilters.state
      }
    }

    return Object.keys(columnFilters).length ? columnFilters : null
  }, [filterModel, quickFilter, usesTimelineDateFilter])

  const applyClientSideFilters = useCallback(() => {
    if (!isGridValid()) {
      return
    }

    isApplyingGridFilterRef.current = true

    if (usesTimelineDateFilter) {
      gridApi.setFilterModel(getColumnFilterModel())
    } else {
      const activeFilter = { ...filterModel, ...quickFilter }
      gridApi.setFilterModel(Object.keys(activeFilter).length ? activeFilter : null)
    }

    gridApi.onFilterChanged()
  }, [gridApi, filterModel, quickFilter, usesTimelineDateFilter, getColumnFilterModel])

  const isTimelineDateFilterPresent = useCallback(() => {
    if (usesTimelineDateFilter && dateFilters?.length) {
      return true
    }

    return Boolean(isExternalFilterPresent?.())
  }, [usesTimelineDateFilter, dateFilters, isExternalFilterPresent])

  const doesTimelineDateFilterPass = useCallback((node) => {
    if (usesTimelineDateFilter && dateFilters?.length && !itemMatchesDateFilters(node.data, dateFilters)) {
      return false
    }

    return doesExternalFilterPass ? doesExternalFilterPass(node) : true
  }, [usesTimelineDateFilter, dateFilters, doesExternalFilterPass])

  useEffect(() => {
    if (location.state?.resetListPosition) {
      pendingExpandedKeyRef.current = null
      dispatch(updateLastSelectedRowIndex(listSettingName, null))
      dispatch(updateLastExpandedRowKey(listSettingName, null))
    }
  }, [location.state?.resetListPosition])

  useEffect(() => {
    if (!isGridValid()) return
    gridApi.paginationGoToPage(pagination.offset)
    gridApi.paginationSetPageSize(pagination.limit)
  }, [pagination.offset, pagination.limit, gridApi])

  useEffect(() => {
    if (!isGridValid() || !shallRefresh) return
    if (rowModelType === ListProps.rowModelTypes.server) {
      gridApi.deselectAll()
      gridApi.refreshServerSide({ purge: true })
    }
    dispatch(setShallRefresh(listSettingName, false))
  }, [shallRefresh])

  // FLAG:useDocuAi — soft refresh: refetch the server rows in place. No purge (rows stay on
  // screen instead of flashing the loading overlay) and no deselectAll, so the analysis-terminal
  // refetches of an upload batch do not tear the list down under the user.
  // This layer is generic and carries NO flag check on purpose (an additive layer must not know
  // about the feature): it is inert unless something dispatches setShallSoftRefresh, and the only
  // producers are useDocuAi-gated — expense-list.tsx and revenue-list.tsx (FILE:ANALYSED handler
  // + reconnect/refocus effect, both behind useDocuAiEnabled) via their ducks' softRefreshList,
  // and receipt-list-uploader.tsx behind isDmsReceiptUploadEnabled(). flag-OFF the flag stays false,
  // this effect never runs, and every legacy caller keeps using the destructive shallRefresh path
  // above. Named here so the safety is greppable from this file instead of only from the callers.
  useEffect(() => {
    if (!isGridValid() || !shallSoftRefresh) return
    if (rowModelType === ListProps.rowModelTypes.server) {
      gridApi.refreshServerSide({ purge: false })
    }
    dispatch(setShallSoftRefresh(listSettingName, false))
    // gridApi is in the deps so a pending shallSoftRefresh=true that arrived before the grid was
    // valid (unmount-surviving async producers: FILE:ANALYSED terminal .then, upload-completion)
    // is consumed as soon as the grid mounts, instead of being stuck true forever (a true→true
    // set is a no-op that would never re-trigger this effect). Inert flag-OFF: no producer ever
    // sets shallSoftRefresh, so the extra runs all early-return.
  }, [shallSoftRefresh, gridApi])

  useEffect(() => {
    return () => {
      if (shouldClearOnUnmount) {
        dispatch(clearList(listSettingName))
      }
    }
  }, [shouldClearOnUnmount])

  useEffect(() => {
    const handleResize = () => {
      onWindowResize()
    }
    if (isGridValid() && refContainer.current) {
      const resizeObserver = new ResizeObserver(handleResize)

      resizeObserver.observe(refContainer.current)

      return () => {
        if (refContainer.current) {
          resizeObserver.unobserve(refContainer.current)
        }
      }
    }
  }, [refContainer.current, gridApi])

  useEffect(() => {
    if (listSettingName && location.state?.refreshData) {
      dispatch(setShallRefresh(listSettingName, true))
    }
  }, [listSettingName, location.state?.refreshData])

  useEffect(() => {
    if (isGridValid() && autoSizeColumns) {
      gridApi.sizeColumnsToFit()
    }
  }, [gridApi, autoSizeColumns])

  const getSelectColumn = (rowSelection) => {
    return {
      headerName: '',
      field: 'select',
      sortable: false,
      suppressResize: true,
      resizable: false,
      suppressMovable: true,
      filter: false,
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      suppressMenu: true,
      headerCheckboxSelection: rowSelection === ROW_SELECTION_MULTIPLE && rowModelType === ListProps.rowModelTypes.client,
      ...(rowModelType !== ListProps.rowModelTypes.server ? { headerCheckboxSelectionFilteredOnly: true } : {}),
      checkboxSelection: true,
      pinned: 'left',
      lockPinned: true,
    }
  }

  useEffect(() => {
    if (isGridValid() && rowModelType === ListProps.rowModelTypes.server && columns.length) {
      if (!shallRefresh) {
        dispatch(setShallRefresh(listSettingName, true))
      }
    }
    setGridInitialized(columns.length > 0)
  }, [columns])

  // when grid data is loaded or changed
  useEffect(() => {
    // show the empty overlay if no items where found
    if (!isGridValid()) return
    if (rowModelType !== ListProps.rowModelTypes.server && items.length <= 0) {
      gridApi.showNoRowsOverlay()
    }

    // adjust columns width after fetching and setting data
    autoSizeColumns && gridApi.sizeColumnsToFit()
  }, [items, gridApi, autoSizeColumns])

  // Server-side lists do not render items, but a failed request sets a new empty items array (LIST_ERROR).
  // Re-applying the filters on it calls onFilterChanged, which refetches — a failing request looped endlessly.
  const clientSideItems = rowModelType === ListProps.rowModelTypes.server ? null : items

  useEffect(() => {
    if (!isGridValid()) return

    window.setTimeout(() => {
      applyClientSideFilters()
    }, 0)
  }, [quickFilter, dateFilters, clientSideItems, gridApi, rowModelType, applyClientSideFilters])

  // server-side lists refetch when quick filter changes
  useEffect(() => {
    if (!isGridValid()) return
    if (rowModelType !== ListProps.rowModelTypes.server) return

    if (!shallRefresh) {
      dispatch(setShallRefresh(listSettingName, true))
    }
  }, [quickFilter, dateFilters, gridApi, rowModelType])

  // global search in list
  useEffect(() => {
    if (!isGridValid()) return
    if (rowModelType === ListProps.rowModelTypes.server) {
      if (!shallRefresh) {
        dispatch(setShallRefresh(listSettingName, true))
      }
    } else {
      gridApi.setQuickFilter(searchTerm)
      const totalCount = gridApi.getDisplayedRowCount()
      dispatch(updatePagination(listSettingName, { totalCount }))
    }
  }, [searchTerm, gridApi])

  useEffect(() => {
    if (!isNumber(props.rowIndex)) {
      closeContextMenu()
    }
  }, [props.rowIndex])

  const onWindowResize = useCallback(() => {
    if (isGridValid()) {
      autoSizeColumns && gridApi.sizeColumnsToFit()
    }
  }, [gridApi, autoSizeColumns])

  const onGridColumnsChanged = ({ api, ...rest }) => {
    if (api) {
      autoSizeColumns && api.sizeColumnsToFit()
    }
  }

  const onGridReady = ({ api, columnApi }) => {
    setGridApi(api)
    setColumnApi(columnApi)
    moveMenuColumnToEnd({ api, columnApi })
    if (rowModelType === ListProps.rowModelTypes.server) {
      const serverSideDataSource = {
        getRows: (params) => {
          dataSource(params).then(result => {
            if (!api?.destroyCalled) {
              if (!result?.data?.length && !result?.meta?.count) {
                api.showNoRowsOverlay()
              } else {
                autoSizeColumns && api.sizeColumnsToFit()
                api.hideOverlay()
              }
            }
          })
        },
      }
      // register dataSource with the grid
      api.setServerSideDatasource(serverSideDataSource)
    } else {
      autoSizeColumns && api.sizeColumnsToFit()
    }

    onListReady && onListReady(api)
  }

  const onFirstDataRendered = useCallback((params) => {
    const { api } = params
    autoSizeColumns && api.sizeColumnsToFit()

    if (lastSelectedRowIndex !== null && lastSelectedRowIndex !== undefined) {
      if (rowModelType === ListProps.rowModelTypes.client) {
        // need to set the previous active page and page size to ensure the row is visible
        const previousActivePage = pagination.offset
        api.paginationGoToPage(previousActivePage)
        api.paginationSetPageSize(pagination.limit)
      }

      api.ensureIndexVisible(lastSelectedRowIndex, 'middle')
      dispatch(updateLastSelectedRowIndex(listSettingName, null))
    }
  }, [listState, listSettingName, dispatch, autoSizeColumns, lastSelectedRowIndex])

  const onSelectionChangedInternal = (params) => {
    const { api } = params
    if (onSelectionChanged) {
      onSelectionChanged(params)
    }
    dispatch(updateSelectedNodes(listSettingName, api.getSelectedNodes()))
  }

  const persistListPosition = (node) => {
    const remembered = getRowToRemember({ node, getRowKey: treeRows?.getRowKey })

    if (!remembered) return

    dispatch(updateLastSelectedRowIndex(listSettingName, remembered.rowIndex))
    // always dispatched, so activating a plain row drops a key remembered earlier
    dispatch(updateLastExpandedRowKey(listSettingName, remembered.expandedRowKey))
  }

  const moveMenuColumnToEnd = ({ columnApi }) => {
    const columns = columnApi.getAllGridColumns()
    columnApi.moveColumn('menu', columns.length - 1)
  }

  const closeContextMenu = () => {
    hideContextMenuCell()
    dispatch(hideContextMenu())
    /*
      Necessary because closing context menu with click in window
      causes white space between last and menu column
    */
    if (!isGridValid()) return
    autoSizeColumns && gridApi.sizeColumnsToFit()
  }

  const getContextMenuCellElement = (rowIndex) => {
    return document.querySelector(`[data-rowindex="${rowIndex}"]`)
  }

  const showContextMenuCell = (rowIndex) => {
    const contextMenuCellEl = getContextMenuCellElement(rowIndex)
    return contextMenuCellEl.classList.add('active')
  }

  const hideContextMenuCell = () => {
    const contextMenuCellEl = document.querySelector('.contextMenuCell.active')
    if (!contextMenuCellEl) return
    contextMenuCellEl.classList.remove('active')
  }

  const onRowClicked = (gridData) => {
    const { event, node, data } = gridData
    const { target } = event

    if (shouldSkipRowClick({ target })) {
      return
    }

    // a child row carries another kind of data, forwarding it to onRowClicked would misread it
    if (treeRows && node?.level > 0) {
      persistListPosition(node)
      treeRows.onChildRowClicked?.({ event, data, node })
      return
    }

    const { rowIndex: propRowIndex, onRowClicked } = props
    const { target: { classList: targetClassList } } = event
    const rowIndex = node.__objectId

    persistListPosition(node)

    // check if context menu cell was clicked
    if (hasContextMenu && (targetClassList.contains('contextMenuCellIcon') || targetClassList.contains('contextMenuCell'))) {
      if (uniformRightClickAndKebabContextMenu && customRightClickContextMenu) return
      else {
        closeContextMenu()
        if (isNumber(propRowIndex) && propRowIndex === rowIndex) {
          return
        }

        showContextMenuCell(rowIndex)
        return dispatch(showContextMenu(rowIndex, data, node))
      }
    }

    if (Boolean(onRowClicked) && !suppressRowClickSelection && !event.shiftKey && !event.ctrlKey) {
      onRowClicked({ event, data, rowIndex, node })
    }
  }

  const onSortChanged = (event) => {
    const { onSortChanged } = props
    const { columnApi } = event
    const newSortedColumns = columnApi.getAllGridColumns()
    const data = newSortedColumns
      .filter(column => column.colId !== 'menu' && column.colId !== 'select' && column.sort)
      .map((column) => {
        const {
          colDef: { name },
          sort,
        } = column

        return {
          name,
          direction: sort,
        }
      })
    onSortChanged && onSortChanged(data)

    if (isGridValid() && gridApi) {
      // Sorting must not drop the selection. Server-side rows are refetched on
      // sort, so keep clearing there; for client-side lists getRowId is stable
      // and ag-grid keeps the selection across a sort – re-sync Redux to it.
      if (rowModelType === ListProps.rowModelTypes.server) {
        gridApi.deselectAll()
        dispatch(updateSelectedNodes(listSettingName, []))
      } else {
        dispatch(updateSelectedNodes(listSettingName, gridApi.getSelectedNodes()))
      }
    }
  }

  const handleColumnMoved = (params) => {
    const { source } = params

    if (source === 'api') {
      return
    }

    const { onColumnMoved } = props

    // secures that the menu cell is always at the end
    const newSortedColumns = columnApi.getAllGridColumns()
    const data = newSortedColumns
      .filter(column => column.colId !== 'menu')
      .map((column) => {
        const {
          active,
          label,
          name,
          required,
          width,
        } = column.colDef

        return {
          active,
          label,
          name,
          required,
          width,
        }
      })

    onColumnMoved && onColumnMoved(data)
  }

  const onFilterChanged = (event) => {
    const { api } = event

    if (rowModelType !== ListProps.rowModelTypes.server && api) {
      if (api?.rowModel?.rowsToDisplay?.length === 0) {
        api.showNoRowsOverlay()
      } else {
        api.hideOverlay()
      }
    }

    if (selectedNodes?.length) {
      api.deselectAll()
      api.forEachNodeAfterFilter(node => selectedNodes.findIndex(s => s.data.id === node.data.id) > -1 && node.setSelected(true))
    }

    const count = api.rowModel?.rowsToDisplay?.length

    if (isApplyingGridFilterRef.current) {
      isApplyingGridFilterRef.current = false
      dispatch(updatePagination(listSettingName, { count }))
      api.refreshHeader()
      return
    }

    const currentFilterModel = usesTimelineDateFilter
      ? { ...api.getFilterModel() }
      : api.getFilterModel()

    if (Object.prototype.hasOwnProperty.call(currentFilterModel, 'tags')) {
      currentFilterModel.tags.filterModels = currentFilterModel.tags.filterModels.filter((item) => item?.values?.length > 0)
    }

    if (usesTimelineDateFilter) {
      delete currentFilterModel[DATE_FILTER_MODEL_KEY]

      if (!quickFilter) {
        delete currentFilterModel.state
      }

      if (dateFilters?.length && filterModel?.[DATE_FILTER_MODEL_KEY]) {
        currentFilterModel[DATE_FILTER_MODEL_KEY] = filterModel[DATE_FILTER_MODEL_KEY]
      }
    }

    dispatch(updateFilterModel(listSettingName, Object.keys(currentFilterModel).length ? currentFilterModel : null))
    dispatch(updatePagination(listSettingName, { count }))
    api.refreshHeader()
  }

  const customLang = {
    loadingOoo: loaderText,
    noRowsToShow: emptyText,
    ctrlC: ' ',
  }

  const getRightClickContextMenuItems = (params) => {
    const { node } = params

    // The entries of a list act on its own kind of row, a child row is none. Returning undefined
    // suppresses the menu, which covers the kebab as well when both are wired to the same callback.
    if (treeRows && node?.level > 0) {
      return undefined
    }

    persistListPosition(node)
    return !disableRightClickContextMenu && customRightClickContextMenu?.(params)
  }

  /**
   * Adapts one column definition to tree rows. A child row carries a different kind of data than the
   * rows the column was written for, so the consumer's renderer takes over for those rows while the
   * column itself, its width, its position and its header stay exactly as they are.
   */
  const applyTreeRowsToColumn = (column) => {
    const { toggleColumn, childCellRendererProps, getChildColSpan } = treeRows

    const withChildRenderer = {
      ...column,
      // undefined falls back to the column's own cellRenderer, which is what the parent rows keep
      cellRendererSelector: (params) => params.node?.level > 0
        ? { component: TREE_CHILD_CELL_RENDERER, params: childCellRendererProps }
        : undefined,
      colSpan: (params) => (params.node?.level > 0 ? getChildColSpan?.(params) ?? 1 : 1),
    }

    if (column.field !== toggleColumn) {
      return withChildRenderer
    }

    return {
      ...withChildRenderer,
      /*
        agGroupCellRenderer contributes the chevron and the indentation and keeps the column's own
        renderer as its inner renderer. ag-grid special cases tree data in its group cell renderer,
        which is why this works on a regular column and needs no hierarchy column of its own.
        Careful: the cell renders nothing at all, chevron included, when its value is null.
      */
      showRowGroup: true,
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        ...column.cellRendererParams,
        innerRenderer: column.cellRenderer,
        // tree data would otherwise append the number of children to the value
        suppressCount: true,
      },
    }
  }

  // display only filter menu for all columns as default
  const columnDefs = useMemo(() => {
    if (!columns) return []

    const menuColumnExists = columns.find(column => column.field === 'menu')
    if (!menuColumnExists && showMenuContextMenu) {
      columns.push({
        ...menuColumn,
        cellRendererParams: {
          simulateContextMenuOnClick: uniformRightClickAndKebabContextMenu,
          actions: contextMenuQuickActions,
        },
      })
    }

    const mappedColumns = columns.map((column, index) => {
      if (autoSizeColumns) {
        // need to set it to 100 and let ag-grid handle the rest
        // before we could set it to the columns width but we now save the column width in the user list setting if he disabled auto size columns
        column.minWidth = 100
      }
      return merge({ menuTabs: ['filterMenuTab'] }, {
        ...column,
        flex: autoSizeColumns ? 1 : 0,
        listSettingName,
        filterParams: {
          ...column.filterParams,
          maxNumConditions: column.name === 'date' ? 16 : 1,
        },
      })
    })

    const allColumns = disableCheckboxes
      ? mappedColumns
      : [
        getSelectColumn(rowSelection),
        ...mappedColumns,
      ]

    return treeRows ? allColumns.map(applyTreeRowsToColumn) : allColumns
  }, [columns, autoSizeColumns, treeRows])

  const defaultColDef = useMemo(() => {
    return {
      sortable: enableSorting,
      resizable: true,
      filter: 'agTextColumnFilter',
      suppressDragLeaveHidesColumns: true,
      headerComponentParams: { menuIcon: 'fa-bars' },
      flex: 1,
      filterParams: {
        closeOnApply: true,
        suppressMiniFilter: true,
        maxNumConditions: 1,
        buttons: ['reset', 'apply'],
      },
      tooltipComponent: 'customTooltip',
    }
  }, [])

  /**
   * Translates the generic treeRows prop into AG Grid tree data options.
   * Returns an empty object when the prop is absent, so nothing changes for lists that do not use
   * the feature.
   *
   */
  const treeRowsSettings = useMemo(() => {
    if (!treeRows) return {}

    const { isRowExpandable, isChildRow, getRowKey, childLoaderText } = treeRows

    return {
      treeData: true,
      /*
        Opens the row the user had open before they left the list. ag-grid asks this for every row node
        it creates, which is why the purge on entering the list is what makes the restore work at all.
        Returning true only sets the expanded flag; the children are fetched once the row is scrolled
        into view.
      */
      isServerSideGroupOpenByDefault: ({ data }) =>
        shouldOpenByDefault(pendingExpandedKeyRef.current, getRowKey(data)),
      // suppresses the hierarchy column ag-grid would add, the toggle column carries the chevron
      treeDataDisplayType: 'custom',
      /*
        Called for every row, children included. Without the isChildRow guard a child would get a
        chevron of its own and asking for its children would produce another datasource request.
      */
      isServerSideGroup: (data) => !isChildRow(data) && Boolean(isRowExpandable(data)),
      getServerSideGroupKey: (data) => getRowKey(data),
      /*
        Hides the checkbox on child rows, they are not part of what an action on the list acts upon.
        Keyed on the level as well, because a row still loading carries no data to judge by.
      */
      isRowSelectable: (node) => !(node?.level > 0) && !isChildRow(node?.data),
      /*
        ag-grid's own loading renderer takes its text from localeText.loadingOoo and asks nothing about
        the row, so a child level would announce itself with the text of the list. Returning undefined
        for the top level leaves that renderer, and therefore the list's own text, untouched.
      */
      ...(childLoaderText
        ? {
          loadingCellRendererSelector: (params) => params.node?.level > 0
            ? { component: TREE_CHILD_LOADING_CELL_RENDERER, params: { text: childLoaderText } }
            : undefined,
        }
        : {}),
    }
  }, [treeRows])

  /**
   * Completes the restore: once the remembered row is open again the key has done its job and is
   * dropped, ref included. Keeping it would re-open the row after the next purge, which sorting and
   * filtering both trigger, undoing a collapse the user just performed.
   */
  const onRowGroupOpened = ({ node }) => {
    if (!treeRows || !node?.expanded || !node.data) return
    if (!shouldOpenByDefault(pendingExpandedKeyRef.current, treeRows.getRowKey(node.data))) return

    pendingExpandedKeyRef.current = null
    dispatch(updateLastExpandedRowKey(listSettingName, null))
  }

  const onPaginationChanged = ({ api }) => {
    let newPagination

    if (rowModelType === ListProps.rowModelTypes.server) {
      newPagination = !suppressServerSideInfiniteScroll
        ? {
          pageCount: api.paginationGetTotalPages(),
          offset: api.paginationGetCurrentPage(),
        }
        : {
          pageCount: api.paginationGetTotalPages(),
          offset: api.paginationGetCurrentPage(),
          totalCount: api.getModel().gridOptionsWrapper.gridOptions.rowData.length,
          count: api.rowModel?.rowsToDisplay?.length,
        }
    } else {
      newPagination = {
        pageCount: api.paginationGetTotalPages(),
        offset: api.paginationGetCurrentPage(),
        totalCount: api.getModel().gridOptionsService.gridOptions.rowData.length,
        count: api.rowModel?.rowsToDisplay?.length,
      }
    }

    dispatch(updatePagination(listSettingName, newPagination))
  }
  const hasAggregation = columns.reduce((result, col) => !!col.aggFunc || result, false)
  const hasSubtitle = !!columns.find(column => column.subtitle)

  const classProps = props.getRowClass
    ? { getRowClass: (params) => clsx(props.rowClass, props.getRowClass(params)) }
    : { rowClass: props.rowClass }

  const gridSettings = {
    ...classProps,
    modules: [
      InfiniteRowModelModule,
      ClientSideRowModelModule,
      SetFilterModule,
      ExcelExportModule,
      MenuModule,
      ClipboardModule,
      MultiFilterModule,
      ServerSideRowModelModule,
      // required by tree data, ag-grid refuses to enable it without the row grouping module
      RowGroupingModule,
    ],
    rowData: items || [],
    columnDefs,
    popupParent,
    postProcessPopup: function(params) {
      const isTwoLastColumn = params.column && params.columnApi.getAllDisplayedVirtualColumns()
        .findIndex(x => x.colId === params.column.colId) >= params.columnApi.getAllDisplayedVirtualColumns().length - 4

      if (params.type !== 'columnMenu') {
        return
      }
      if (isTwoLastColumn) {
        const ePopup = params.ePopup
        const oldLeftStr = ePopup.style.left
        const oldLeft = parseInt(oldLeftStr)
        const newLeft = oldLeft - 55
        ePopup.style.left = newLeft + 'px'
      }
    },
    icons,
    rowSelection,
    sortingOrder: ['asc', 'desc'],
    suppressRowClickSelection,
    rowMultiSelectWithClick,
    suppressPropertyNamesCheck: true,
    suppressDragLeaveHidesColumns: true,
    enableColResize: true,
    enableServerSideSorting: true,
    animateRows: false,
    // Present ONLY when a caller opts in — otherwise the key is absent and ag-grid uses its
    // default, so lists that don't pass the prop are byte-identical to before.
    ...(suppressAnimationFrame ? { suppressAnimationFrame: true } : {}),
    headerHeight: (hasAggregation || hasSubtitle) ? 69.5 : null,
    defaultColDef,
    tooltipShowDelay: 500,
    colResizeDefault: autoSizeColumns ? 'shift' : null,
    noRowsOverlayComponent: emptyComponent ? 'customNoRowsOverlay' : null,
    components: {
      ...listCellRenderers,
      listTagFilter: ListTagFilter,
      ...components,
      contextMenuCellRenderer: hasContextMenu ? ContextMenuCell : null,
      customNoRowsOverlay: emptyComponent || null,
      agColumnHeader: ListColumnHeader,
      agDateInput: ListDateInput,
      [TREE_CHILD_CELL_RENDERER]: treeRows?.childCellRenderer || null,
      [TREE_CHILD_LOADING_CELL_RENDERER]: TreeChildLoadingCell,
    },
    localeText: { ...lang.list, ...customLang },
    onColumnMoved: handleColumnMoved,
    onSortChanged,
    onRowClicked,
    onCellClicked,
    onGridColumnsChanged,
    onGridReady,
    onColumnResized: debounce((params) => {
      try {
        const { columnApi, source } = params
        if (['autosizeColumns', 'uiColumnResized'].includes(source)) {
          const newSortedColumns = columnApi.getAllGridColumns()
          saveColumnWidthInUserListSetting(listSettingName, newSortedColumns)
        }
      } catch (err) {
        // swallow error
      }
    }, 500),
    onComponentStateChanged: moveMenuColumnToEnd,
    pagination: usePagination,
    suppressPaginationPanel: true,
    onFilterChanged,
    onFirstDataRendered,
    onRowGroupOpened,
    getContextMenuItems: getRightClickContextMenuItems,
    processCellForClipboard,
    onSelectionChanged: onSelectionChangedInternal,
    onCellMouseDown: ({ event, api, colDef }) => {
      const { onRowClicked } = props

      // Do not override when the list explicitly suppresses row-click selection (e.g. editable cell grids).
      if (colDef.field !== 'select' && !suppressRowClickSelection) {
        api.gridOptionsService.gridOptions.suppressRowClickSelection = Boolean(onRowClicked) && !event.shiftKey && !event.ctrlKey
      }
    },
    onPaginationChanged,
    isExternalFilterPresent: isTimelineDateFilterPresent,
    doesExternalFilterPass: doesTimelineDateFilterPass,
    excelStyles: ListDefaultExcelStyles,
    sideBar: showToolPanel ? { toolPanels } : undefined,
    getRowId: ({ data, level, parentKeys }) => {
      const id = data?.rowKey || (data?.id ? String(data.id) : String(Math.floor(Math.random() * 1000001)))

      /*
        Child rows come from another source than the rows above them, so their ids stem from an
        independent sequence and can collide with the ones already in the grid. level and parentKeys
        are filled by the server side row model only, the client side model keeps the plain id.
      */
      return level > 0 && parentKeys?.length ? [...parentKeys, id].join('/') : id
    },
  }
  let ssrSettings = {}
  let exportGrid = null
  const onExportGridReady = (params) => {
    params.api.exportDataAsExcel(exportParams)
    dispatch(setShallExport(listSettingName, false))
  }

  if (rowModelType === ListProps.rowModelTypes.server) {
    ssrSettings = {
      rowModelType,
      suppressServerSideInfiniteScroll,
      cacheBlockSize: 20,
    }
    exportGrid = shallExport && (
      <div style={{ display: 'none' }}>
        <AgGridReact {...gridSettings}
          rowData= {exportItems || []}
          onGridReady={onExportGridReady}
        />
      </div>
    )
  }

  if (!gridInitialized || isLoading) {
    return (<Loader text={loaderText} />)
  }

  return (
    <div
      className={clsx(
        'ag-theme-material',
        { 'hide-menu-tab-header': hideMenuTabHeader },
        classes.container,
        { [classes.treeRows]: Boolean(treeRows) },
      )}
      ref={refContainer}>
      <AgGridReact {...gridSettings} {...ssrSettings} {...treeRowsSettings} />
      {usePagination && <PaginationV2 {...pagination} listSettingName={listSettingName} />}
      {contextMenu}
      {exportGrid}
    </div>
  )

}

List.propTypes = {
  items: PropTypes.array,
  columns: PropTypes.array,
  emptyText: PropTypes.string, // text if no items exist
  loaderText: PropTypes.string, // text for loading
  rowIndex: PropTypes.number, // index of row with open context menu
  onRowClicked: PropTypes.func,
  onCellClicked: PropTypes.func,
  onColumnMoved: PropTypes.func,
  onListReady: PropTypes.func,
  onSortChanged: PropTypes.func,
  sortChanged: PropTypes.func,
  offsetTop: PropTypes.number,
  fetchOnInit: PropTypes.bool,
  showMenuContextMenu: PropTypes.bool,
  rowSelection: PropTypes.oneOf([ROW_SELECTION_SINGLE, ROW_SELECTION_MULTIPLE]),
  suppressRowClickSelection: PropTypes.bool,
  rowMultiSelectWithClick: PropTypes.bool,
  contextMenu: PropTypes.node,
  customRightClickContextMenu: PropTypes.func,
  disableRightClickContextMenu: PropTypes.bool,
  hideMenuTabHeader: PropTypes.bool,
  processCellForClipboard: PropTypes.func,
  onSelectionChanged: PropTypes.func,
  isExternalFilterPresent: PropTypes.func,
  doesExternalFilterPass: PropTypes.func,
  disableCheckboxes: PropTypes.bool,
  pagination: PropTypes.object,
  listSettingName: PropTypes.string.isRequired,
  usePagination: PropTypes.bool,
  enableSorting: PropTypes.bool,
  rowModelType: PropTypes.string,
  suppressServerSideInfiniteScroll: PropTypes.bool,
  suppressAnimationFrame: PropTypes.bool,
  toolPanels: PropTypes.arrayOf(PropTypes.string),
  dataSource: PropTypes.func,
  showToolPanel: PropTypes.bool,
  shouldClearOnUnmount: PropTypes.bool,
  components: PropTypes.object,
  emptyComponent: PropTypes.func,
  uniformRightClickAndKebabContextMenu: PropTypes.bool,
  contextMenuQuickActions: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    action: PropTypes.func.isRequired,
  })),
  getRowClass: PropTypes.func,
  rowData: PropTypes.array,
  popupParent: PropTypes.instanceOf(HTMLElement), // ag-grid popupParent for column filter/menu popups
  /*
    Turns rows of the list into parents of further rows, using its own columns for both. The children
    are served by the same dataSource, which ag-grid calls again with request.groupKeys set to the
    keys returned by getRowKey.
  */
  treeRows: PropTypes.shape({
    isRowExpandable: PropTypes.func.isRequired, // (data) => boolean, asked for parent rows
    isChildRow: PropTypes.func.isRequired, // (data) => boolean, asked for every row
    getRowKey: PropTypes.func.isRequired, // (data) => string, the key the children are fetched with
    toggleColumn: PropTypes.string, // field of the column that carries the chevron
    childCellRenderer: PropTypes.elementType.isRequired, // renders every cell of a child row
    childCellRendererProps: PropTypes.object, // passed on to that renderer
    childLoaderText: PropTypes.string, // shown while a child level loads, defaults to the list's text
    getChildColSpan: PropTypes.func, // (colSpanParams) => number, defaults to one column
    onChildRowClicked: PropTypes.func, // ({ event, data, node }) => void
  }),
}

export {
  List as default,
  ROW_SELECTION_SINGLE,
  ROW_SELECTION_MULTIPLE,
  ListProps,
}

// TODO sorting and filtering
